import { ROLE_ASSISTANT, ROLE_SYSTEM, ROLE_USER, DOC_LIMIT, DOC_SIZE_LIMIT, FILE_INPUT_ACCEPT, IMAGE_LIMIT, IMAGE_SIZE_LIMIT, TOOL_RUN_AGENT, TOOL_SEARCH_ANALYST } from "./LLMClient";
import { observer } from "mobx-react";
import { ToolUseBlock } from "./ToolUseBlock";
import FileAttachments from "../components/controls/FileAttachments";
import { blunt } from "../tools";
import { abbreviateLines, safeParseJSON, uploadFile } from "../lib/utils";
import { Fragment, useContext, useEffect, useMemo, useState, memo, useRef } from "react";
import { MobXProviderContext } from "mobx-react";
import { AgentConversation, Conversation } from "./Conversation";
import Avatar from "../components/Avatar";
import { SafeRenderer } from "../components/SafeRenderer";
import Textarea from "react-expanding-textarea";
import "../styles/SearchBox.css"; // For chat-box-control styles

import { renderLLMMarkdown } from "./renderLLMMarkdown";
import { interpolateSourceLinks } from "../markdown/interpolation";
import SourceIconInjector from "../components/SourceIconInjector";
import { useConversationContext } from "../contexts/ConversationContext";
import SourceLinkContext from "../contexts/SourceLinkContext";
import { extractUrlContext } from "../markdown/urlContextExtraction";
import { useStreamingTypewriter } from "../hooks/useTypewriter";
import CitationChips from "../components/CitationChips";
import { citationsFromToolResult } from "../lib/analystCitations";
import { SOURCE_ENTITY } from "../lib/sourceMetadata";
import { generatedImageSrc } from "./generatedImage";

const SMILES_QUERY_RE = /\b(smiles|query):([\S]+)/g;

// Search buttons/links are derived entirely from a search-analyst's own "search" toolUse
// input (ids + params), never from LLM-written prose — so they must surface regardless of
// how deeply the search-analyst call is nested (e.g. guide -> run_agent flavor -> search).
// Recurses into any nested agentMessages so future multi-level dispatch chains stay covered.
const findNestedSearchToolUses = (turns) => {
  const found = [];
  (turns || []).forEach(turn => {
    (turn?.content || []).forEach(block => {
      if (block?.toolUse?.name === TOOL_SEARCH_ANALYST && block.toolUse.input?.ids?.length) {
        // Carry this call's own agentMessages along with it, so ToolUseBlock can resolve
        // its own real run_search leaf (ids/params actually searched) rather than this
        // call's pre-expansion orchestration input.
        found.push({ toolUse: block.toolUse, agentMessages: block.agentMessages });
      }
      if (block?.agentMessages?.length) {
        found.push(...findNestedSearchToolUses(block.agentMessages));
      }
    });
  });
  return found;
};

const renderUserTextSegment = (text) => {
  if (!SMILES_QUERY_RE.test(text)) return text;
  SMILES_QUERY_RE.lastIndex = 0;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = SMILES_QUERY_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const prefix = match[1];
    const value = match[2];
    parts.push(
      <span key={match.index} className={`chem-ref chem-ref-${prefix}`} title={`${prefix}:${value}`}>{value}</span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 1 ? parts[0] : parts;
};

// Memoized markdown renderer to prevent re-creating the React element tree
// when markdown content hasn't changed. React.memo compares props by reference/value,
// so the component only re-renders when markdown string actually changes.
const MemoizedMarkdown = memo(({ markdown, trapErrors, rawMarkdown }) => {
  return renderLLMMarkdown(markdown, trapErrors, rawMarkdown);
});

/**
 * Extracts ID-to-resolved-URL mappings from resolved markdown.
 * Parses markdown for [text](resolved_url) patterns and extracts original Plex IDs
 * from the link text using the pattern that matches Plex identifiers.
 *
 * @param {string} resolvedMarkdown - Markdown after interpolateSourceLinks has resolved URLs
 * @returns {Map<string, string>} Map of original Plex ID to resolved URL
 *
 * @example
 * // Input: "[[1]](https://www.ebi.ac.uk/chembl/...)"
 * // Output: Map { "chembl:CHEMBL123" => "https://www.ebi.ac.uk/chembl/..." }
 */
function extractIdToUrlMappings(resolvedMarkdown) {
  const idToUrlMap = new Map();

  if (!resolvedMarkdown) {
    return idToUrlMap;
  }

  // Pattern to match markdown links: [text](url)
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;

  let match;
  while ((match = linkPattern.exec(resolvedMarkdown)) !== null) {
    const linkText = match[1];
    const resolvedUrl = match[2];

    // Extract Plex ID from link text using pattern: prefix:identifier
    // Pattern matches: chembl:CHEMBL123, uniprot:P12345, pubmed:12345678, etc.
    const idPattern = /\b([a-z][-_0-9a-z]+:[^\s\)]+)/i;
    const idMatch = linkText.match(idPattern);

    if (idMatch) {
      const plexId = idMatch[1];
      idToUrlMap.set(plexId, resolvedUrl);
    }
  }

  return idToUrlMap;
}


const ContentBlockInner = observer(({
                               role,
                               block,
                               blockNumber,
                               turnNumber,
                               timestamp,
                               turnHasToolUse = false,
                               messageError = null,
                               truncated = false,
                               tokenUsage = null,
                               interrupted = false,
                               trapErrors = false,
                               agentId = null,
                               toolInfo = null,
                               showAdvancedFeatures = false,
                               showDetails = () => {},
                               busy = false,
                               replayConversation = false,
                               discardConversation = false,
                               resolveSourceLinks = false,
                               userStore = null,
                               isStickyPrompt = false,
                               isCurrentlySticky = false,
                               isTruncated = false,
                               expanded = false,
                               onToggleExpand = null,
                               contentRef = null,
                               mentionedGenes = null,
                               renderControlsAtTurnLevel = false,
                               editable = false,
                               existingAttachments = [],
                               onEdit = null,
                               onSave = null,
                               onCancel = null,
                               onVisibilityChange = null,
                             }) => {
  const textareaRef = useRef(null);
  const [localEditText, setLocalEditText] = useState("");
  const flattenAttachment = (item) =>
    item?.file ? {...item.file, uploadId: item.uploadId ?? item.file.uploadId} : item;
  const [editAttachments, setEditAttachments] = useState((existingAttachments || []).map(flattenAttachment));
  const {
    text: blockText = "",
    toolUse = null,
    toolResult = null,
    document = null,
    image = null,
    partial = false,
    rawMarkdown = false,
    reasoningContent = null,
  } = block;
  const [interpolatedText, setInterpolatedText] = useState(null);
  const [urlContextMap, setUrlContextMap] = useState(null);
  const conversation = useConversationContext();
  const isStreamReplaying = conversation?.isStreamReplaying ?? false;
  // During streaming, feed raw text to the typewriter so async interpolation resolves
  // don't cause the typewriter's position to jump (flicker). interpolatedText is still
  // updated via debounced background resolves so the transition when streaming ends is
  // seamless — the final resolve fires on the completed block text.
  const typewriterText = useStreamingTypewriter(partial ? blunt(blockText) : interpolatedText, partial, { drainOnComplete: isStreamReplaying });
  const { agentMessages = null } = toolInfo || {};
  const { status = "success" } = toolResult || {};
  const error = messageError || status === "error";
  const blockErrorClass = error ? " alert alert-error" : "";
  const partialClass = partial ? " partial-block" : "";
  const className = `content-block content-block-${blockNumber}${blockErrorClass}${partialClass}${turnHasToolUse ? " has-tool-use" : ""}`;

  const sourceReferencesStore = conversation?.sourceReferencesStore;
  const { searchSessionStore } = useContext(MobXProviderContext);

  // Hoisted above all conditional returns to satisfy Rules of Hooks
  const datasets = searchSessionStore?.datasets;
  const turnId = `turn-${turnNumber}`;
  const sourceLinkValue = useMemo(() => ({
    sourceReferencesStore,
    turnId,
    agentId,
    urlContextMap,
    datasets,
  }), [sourceReferencesStore, turnId, agentId, urlContextMap, datasets]);

  // Split controls: hover controls at bottom-right for sticky prompts
  const hoverControls = (
    <div className={"content-block-controls"} >
      {role === ROLE_USER && replayConversation && onEdit && (<div
        key={`edit`}
        className={`glyphicon glyphicon-pencil flat-button${busy ? " disabled" : ""}`}
        title={"Edit this prompt"}
        onClick={e => !busy && onEdit()}
      ></div>)}
      {replayConversation && (<div
        key={`replay`}
        className={`glyphicon glyphicon-refresh flat-button${busy ? " disabled" : ""}`}
        title={`${role === ROLE_USER && !discardConversation ? "Continue" : "Regenerate"} the conversation from this point`}
        onClick={e => replayConversation(turnNumber)}
      ></div>)}
      {discardConversation && (<div
        key={`discard`}
        className={`glyphicon glyphicon-trash flat-button${busy ? " disabled" : ""}`}
        title={"Discard conversation from this point"}
        onClick={e => !busy && discardConversation(turnNumber)}
      ></div>)}
    </div>
  );

  // Left side footer controls: ellipsis (expand) or collapse button
  // Only show for sticky prompts when NOT editing
  const stickyLeftControls = isStickyPrompt && !editable && (
    <div className="sticky-prompt-left-controls">
      {!expanded && isTruncated && (
        <div
          className="sticky-prompt-ellipsis"
          onClick={onToggleExpand ? (e) => {
            e.stopPropagation();
            onToggleExpand();
          } : undefined}
          title="Click to expand prompt"
        >
          ...
        </div>
      )}
      {expanded && onToggleExpand && (
        <div
          key={`collapse`}
          className={`glyphicon glyphicon-triangle-top flat-button`}
          title={"Collapse prompt"}
          onClick={e => {
            e.stopPropagation();
            onToggleExpand();
          }}
        />
      )}
    </div>
  );

  // Only render controls at content-block level if not rendering at turn level
  const controls = !isStickyPrompt && !renderControlsAtTurnLevel ? hoverControls : null;

  // Initialize local edit text and attachments when entering edit mode
  // Need to keep this hook ahead of conditionals (Rules of Hooks)
  useEffect(() => {
    if (editable) {
      const trimmed = blockText.trim();
      setLocalEditText(trimmed);
      setEditAttachments((existingAttachments || []).map(flattenAttachment));
      // Focus textarea
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [editable, blockText]);

  // Need to keep this hook ahead of conditionals
  useEffect(() => {
    // During streaming, debounce interpolation so source links resolve periodically
    // rather than on every delta. The typewriter reads raw blockText during streaming
    // so these background resolves don't cause flicker. When streaming ends (partial=false)
    // the effect fires immediately with no debounce for a clean final resolve.
    const STREAMING_DEBOUNCE_MS = 500;
    const delay = partial ? STREAMING_DEBOUNCE_MS : 0;

    const timer = setTimeout(() => {
      if (blockText && resolveSourceLinks) {
        const originalText = blunt(blockText);

        // Extract context BEFORE link resolution (maps context to Plex IDs)
        const urlContextMapBeforeResolution = extractUrlContext(originalText);

        interpolateSourceLinks(originalText, resolveSourceLinks, mentionedGenes)
          .then(({ text: resolved, mentions, descriptions }) => {
            setInterpolatedText(resolved);
            // PLEX-5920: the linked entities' own descriptions, for their source cards (the
            // evidence tool results' are registered by useSourceMetadata in ChatSession)
            sourceReferencesStore?.registerDescriptions?.(descriptions, SOURCE_ENTITY);

            // Merge new mentions back into the shared conversation-wide set
            if (mentionedGenes && mentions) {
              for (const id of mentions) mentionedGenes.add(id);
            }

            // Extract ID-to-URL mappings from resolved markdown
            const idToUrlMap = extractIdToUrlMappings(resolved);

            // Map context from IDs to resolved URLs
            const urlContextMap = new Map();
            for (const [plexId, context] of urlContextMapBeforeResolution.entries()) {
              const resolvedUrl = idToUrlMap.get(plexId);
              if (resolvedUrl) {
                urlContextMap.set(resolvedUrl, context);
              }
            }

            setUrlContextMap(urlContextMap);
          });
      }
      else {
        setInterpolatedText(blunt(blockText));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [blockText, partial, resolveSourceLinks, mentionedGenes, sourceReferencesStore])

  // Bedrock's streamed ImageBlockDelta (image generation) carries source.bytes, distinct
  // from a user-uploaded attachment's source.file — only the latter renders via FileAttachments.
  const isGeneratedImage = image && !image.source?.file;
  if (document || (image && !isGeneratedImage)) {
    const block = document || image;
    const sourceFile = block.source?.file || {};
    const fileObj = {
      name: sourceFile.name || block.name || 'attachment',
      size: sourceFile.size || 0,
      lastModified: sourceFile.lastModified || 0,
      type: sourceFile.type || '',
      uploadId: sourceFile.uploadId,
    };
    return (<FileAttachments value={[{file: fileObj, error: block.error, errorMessage: block.errorMessage, uploadId: fileObj.uploadId}]} readOnly={true} />);
  }
  const isAdmin = userStore?.isAdmin || false;
  if (isGeneratedImage) {
    // PLEX-6049: a generated image is shown once complete (each ImageBlockDelta carries the
    // whole source, so there is nothing to show progressively). Only base64 bytes with a known
    // format can be drawn; anything else stays an admin-only debug view.
    const src = generatedImageSrc(image);
    if (src && !partial) {
      return (
        <div className={`${className} generated-image-block`} title={timestamp}>
          <img src={src} alt="Generated image"/>
        </div>
      );
    }
    if (partial) {
      return (
        <div className={`${className} generated-image-block`} title={timestamp}>
          <span className="status-message">Generating image...</span>
        </div>
      );
    }
    if (isAdmin) {
      return (
        <div className={`${className} generated-image-block`} title={timestamp}>
          <pre>{JSON.stringify(image, null, 2)}</pre>
        </div>
      );
    }
    return null;
  }
  if (!blockText && !toolUse && !toolResult) {
    // Reasoning-only block (Fable 5 thinking): show indicator while streaming; once complete,
    // admins can see the full reasoning text (WEBAPP-1D follow-up — not shown to regular users).
    if (reasoningContent && partial) {
      return (
        <div className={`${className} reasoning-block`} title={timestamp}>
          <span className="status-message">Thinking...</span>
        </div>
      );
    }
    if (reasoningContent && isAdmin) {
      return (
        <div className={`${className} reasoningContent`} title={timestamp}>
          <pre>{reasoningContent.text}</pre>
        </div>
      );
    }
    return null;
  }
  if (role === ROLE_ASSISTANT) {
    if (toolUse) {
      // Always render ToolUseBlock for tool use (e.g., search buttons)
      // ToolUseBlock handles its own visibility logic based on showAdvancedFeatures
      // Also render AgentConversation here while sub-agent is live (toolResult block arrives later)
      // Show live AgentConversation only while the assistant turn is partial (sub-agent running).
      // Once complete, the corresponding toolResult block on the user turn takes over.
      // Show AgentConversation as soon as the tool call starts (partial=true),
      // even before any sub-agent events arrive — so the user sees activity immediately.
      // PLEX-5919: Only show here when partial — the `|| agentMessages.length > 0` branch
      // kept this visible after completion, producing a duplicate row alongside the
      // toolResult block's own AgentConversation on the following user turn.
      const isLiveAgent = partial;
      // A flavor agent (run_agent) is a peer of the guide, not a search-analyst detail —
      // render its turns inline at top level (own styling) rather than in the collapsible
      // AgentConversation used for search-analyst dispatches.
      const isFlavorAgent = toolUse.name === TOOL_RUN_AGENT;
      return (
        <Fragment>
          <ToolUseBlock
            agentId={agentId}
            toolUse={toolUse}
            agentMessages={agentMessages}
            className={className}
            trapErrors={trapErrors}
            showAdvancedFeatures={showAdvancedFeatures}
            isRetry={toolInfo?.isRetry}
          />
          {isLiveAgent && (isFlavorAgent ? (
            <Conversation
              className="flavor-agent"
              turns={agentMessages || []}
              agentId={toolUse.toolUseId}
              showTimestamps={false}
              showAdvancedFeatures={showAdvancedFeatures}
              showDetails={showDetails}
              resolveSourceLinks={resolveSourceLinks}
              busy={true}
            />
          ) : (
            <AgentConversation
              turns={agentMessages || []}
              agentId={toolUse.toolUseId}
              showAdvancedFeatures={showAdvancedFeatures}
              showDetails={showDetails}
              toolName={toolUse.name}
              busy={true}
              replayConversation={replayConversation}
              turnNumber={turnNumber}
            />
          ))}
        </Fragment>
      );
    }
    // Check if this text block should be suppressed (final answer after tool use)
    // Skip suppression for power users and admins
    if (block.suppressFinalAnswer && !showAdvancedFeatures) {
      // Show status message instead of the actual text
      const statusText = partial ? "Summarizing results..." : "Results summarized";
      return (
        <div className={`${className} suppressed-final-answer`} title={timestamp}>
          <span className="status-message">{statusText}</span>
        </div>
      );
    }
    const markdown = partial ? (typewriterText || '') : (interpolatedText || blunt(blockText));
    if (!markdown) {
      return null;
    }

    return (
      <div className={`${className}`} title={timestamp}>
        <SourceLinkContext.Provider value={sourceLinkValue}>
          {sourceReferencesStore ? (
            <SourceIconInjector store={sourceReferencesStore} onVisibilityChange={onVisibilityChange}>
              <MemoizedMarkdown key={partial ? 'partial' : 'complete'} markdown={markdown} trapErrors={trapErrors} rawMarkdown={rawMarkdown} />
            </SourceIconInjector>
          ) : (
            <MemoizedMarkdown key={partial ? 'partial' : 'complete'} markdown={markdown} trapErrors={trapErrors} rawMarkdown={rawMarkdown} />
          )}
        </SourceLinkContext.Provider>
        {truncated ? (<span className="truncated">(response length limit reached{tokenUsage ?? ""})</span>) : ""}
        {!partial && controls}
      </div>
    );
  }
  if (role === ROLE_USER) {
    if (toolResult) {
      if (!toolInfo) {
        return null;
      }
      const { toolUseId } = toolResult;
      const isAgent = agentMessages && agentMessages.length > 0;
      const toolResultDetailsTitle = (toolResult?.content?.map(b => b.text || (b.json && JSON.stringify(b.json)) || "").join("\n"))
                                     || "Results being generated...";
      const toolResultContentText = toolResult?.content?.filter(b => b.text).map(b => b.text).join("\n");
      const toolResultText = partial
                               ? "Working..."
                               : toolResultContentText || (error ? "An error occurred." : "");
      const toolResultDetails = interrupted
                                    ? "User interrupted"
                                    : `${toolResult.content?.length ? "Results" : "Partial results"} received for `;
      // Whatever dispatch tool the outer turn used (run_agent, run_analyst, or any future
      // rename), a nested search-analyst call is what actually matters here — look for it
      // directly rather than matching the outer tool's name, so this doesn't need updating
      // every time a new dispatch-tool name is introduced (PLEX-6102).
      const nestedSearchToolUses = findNestedSearchToolUses(agentMessages);
      // Same distinction as the live/partial branch above (PLEX-6289): a flavor agent
      // (run_agent) is a peer of the guide, not a search-analyst detail. Without this
      // check here too, a flavor agent's response rendered top-level (via the plain
      // Conversation branch) while streaming would collapse into the search-analyst-
      // style AgentConversation accordion the instant the toolResult arrived — jarring,
      // and the opposite of "part of the top-level conversation."
      const isFlavorAgent = toolInfo?.toolUse?.name === TOOL_RUN_AGENT;
      // What a search-analyst answer cites (PLEX-6500), once its result is in
      const citations = partial ? null : citationsFromToolResult(toolResult);
      return (
        <Fragment>
          {nestedSearchToolUses.map(({ toolUse: nestedToolUse, agentMessages: nestedAgentMessages }) => (
            <ToolUseBlock
              key={`nested-search-${nestedToolUse.toolUseId}`}
              agentId={agentId}
              toolUse={nestedToolUse}
              agentMessages={nestedAgentMessages}
              className={className}
              trapErrors={trapErrors}
              showAdvancedFeatures={showAdvancedFeatures}
            />
          ))}
          {isAgent && (isFlavorAgent ? (
            <Conversation
              className="flavor-agent"
              turns={agentMessages}
              agentId={toolUseId}
              showTimestamps={false}
              showAdvancedFeatures={showAdvancedFeatures}
              showDetails={showDetails}
              resolveSourceLinks={resolveSourceLinks}
              busy={partial}
            />
          ) : (
            <AgentConversation
              turns={agentMessages}
              agentId={toolUseId}
              showAdvancedFeatures={showAdvancedFeatures}
              showDetails={showDetails}
              toolName={toolInfo?.toolUse?.name}
              busy={partial}
              replayConversation={replayConversation}
              turnNumber={turnNumber}
            />
          ))}
          {citations && <CitationChips citations={citations} />}
          {agentMessages && showAdvancedFeatures && showDetails && !partial && (
            <div key={`tool-result-text`} title="Tool result" className={`tool-result tool-result-text${className ? " " + className : ""}`}>
              <p className={"agent-result"}>Agent summary result, used for continuation</p>
              <MemoizedMarkdown markdown={toolResultText} trapErrors={trapErrors} />
              {truncated ? (<span className="truncated">(response length limit reached{tokenUsage ?? ""})</span>) : ""}
            </div>)}
          {(showAdvancedFeatures || isAgent) && (
            <div key={`tool-result`}
                 title={abbreviateLines(toolResultDetailsTitle, 2)}
                 className={`${className} tool-result tool-result-details power-user`}>
              <span>{toolResultDetails}<em>{toolInfo?.toolUse.name}</em></span>
              {!partial && controls}
            </div>)}
        </Fragment>
      );
    }
    // Trim blockText to avoid rendering blank prompts due to leading/trailing whitespace
    const trimmedText = blockText.trim();

    // Handle ESC key to cancel editing; Enter to submit (Shift/Alt+Enter = newline)
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel && onCancel();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        if (localEditText.trim()) {
          onSave && onSave(localEditText, editAttachments.length ? editAttachments : null);
        }
      }
    };

    return (
      <div className={`${className}${editable ? " editing" : ""}`} title={timestamp} ref={contentRef}>
        {editable ? (
          <div className="editing-prompt">
            <Textarea
              ref={textareaRef}
              className="prompt"
              value={localEditText}
              onChange={e => setLocalEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Input your replacement prompt or ESC to restore the original"
            />
            <div className="editing-prompt-actions">
              <FileAttachments
                value={editAttachments}
                onChange={files => setEditAttachments(files?.length ? files : [])}
                authData={userStore}
                documentLimit={DOC_LIMIT}
                documentSizeLimit={DOC_SIZE_LIMIT}
                imageLimit={IMAGE_LIMIT}
                imageSizeLimit={IMAGE_SIZE_LIMIT}
                accept={FILE_INPUT_ACCEPT.join(',')}
                uploadFile={uploadFile}
              />
              <button
                className="send"
                disabled={!localEditText.trim()}
                title="Save & Continue"
                onClick={() => localEditText.trim() && onSave && onSave(localEditText, editAttachments.length ? editAttachments : null)}
              >
                <span className="glyphicon glyphicon-ok"></span>
              </button>
              <button
                className="stop"
                title="Cancel"
                onClick={onCancel}
              >
                <span className="glyphicon glyphicon-remove"></span>
              </button>
            </div>
          </div>
        ) : (
          <div className="content-block-text">
            <Avatar userStore={userStore} />
            {trimmedText.split("\n").map((s, pidx) => (<span key={`user-text.${pidx}`}>{renderUserTextSegment(s)}<br className="newline" /></span>))}
          </div>
        )}
        {!editable && isStickyPrompt && !partial && (
          <div className="sticky-prompt-footer">
            {stickyLeftControls}
            {hoverControls}
          </div>
        )}
        {!editable && !isStickyPrompt && !partial && controls}
      </div>
    );
  }
  if (role === ROLE_SYSTEM) {
    const isAdmin = userStore?.isAdmin || false;
    const parsed = safeParseJSON(blockText);
    // Strip leading timestamp prefix e.g. "[2026-03-20T15:13:09.997Z] " for display
    const rawText = parsed?.message || blockText || "";
    const displayText = rawText.replace(/^\[\d{4}-\d{2}-\d{2}T[\d:.]+Z\]\s*/, "");
    // For non-admins, hide internal error details (Improper agent message, raw Bedrock errors)
    const isInternalError = /^Improper agent message|^messages\.\d+:|^tool_use ids/.test(displayText);
    if (isInternalError && !isAdmin) {
      return (
        <div className={`content-block content-block-${blockNumber} system ${blockErrorClass}`}>
          An error occurred. Please try again.
        </div>
      );
    }
    return (
      <div className={`content-block content-block-${blockNumber} system ${blockErrorClass}`}>
        <span>{displayText}</span>
        {isAdmin && parsed?.message && blockText !== parsed.message && (
          <pre className="error-admin-detail">{blockText}</pre>
        )}
      </div>
    );
  }
  return (<div className={"alert alert-error"}>Unknown role "{role}"</div>);
});

export const ContentBlock = (props) => {
  const { blockNumber, turnNumber, block } = props;
  // Use a completely stable resetKey that never changes during the lifetime of a content block
  // The SafeRenderer should only reset when we move to a different block entirely
  // Don't include partial status or text content - these change during streaming
  const resetKey = `${turnNumber}-${blockNumber}`;

  return (
    <SafeRenderer
      errorMessage={`Content block ${blockNumber} render error`}
      resetKeys={[resetKey]}
      className="content-block-error-boundary"
    >
      <ContentBlockInner {...props} />
    </SafeRenderer>
  );
};

